import { createSignal, onMount, Show, onCleanup } from "solid-js";
import { api } from "../shared";
import type { RealtimeDebugResponse } from "../types";

interface ManagerOption {
  userId: string;
  name: string;
  role: string;
}

export function ProjectRealtimeDebugPanel(props: { projectId: string }) {
  const [data, setData] = createSignal<RealtimeDebugResponse | null>(null);
  const [error, setError] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [expanded, setExpanded] = createSignal(false);
  const [managers, setManagers] = createSignal<ManagerOption[]>([]);
  const [selectedManager, setSelectedManager] = createSignal<string>("");
  const [authToken, setAuthToken] = createSignal<string | null>(null);
  const [authLoading, setAuthLoading] = createSignal(false);
  const [orgId, setOrgId] = createSignal<string>("");
  const [paused, setPaused] = createSignal(false);
  const [tokenExpired, setTokenExpired] = createSignal(false);

  async function loadManagers() {
    console.log("[ProjectDebug] loadManagers called", { projectId: props.projectId });
    try {
      console.log("[ProjectDebug] calling generateToken");
      const tokenResult = await api.projects.generateToken(props.projectId, "");
      console.log("[ProjectDebug] generateToken result", tokenResult);
      const organizationId = tokenResult.organizationId || "";
      if (organizationId) {
        console.log("[ProjectDebug] fetching managers for org", organizationId);
        setOrgId(organizationId);
        const mgrs = await api.organizations.listManagers(organizationId);
        console.log("[ProjectDebug] managers loaded", mgrs.length);
        setManagers(mgrs);
        if (mgrs.length > 0) {
          setSelectedManager(mgrs[0].userId);
        }
      } else {
        console.log("[ProjectDebug] no organizationId, showing error");
        setError("Project has no organization. Cannot determine managers.");
      }
    } catch (e: any) {
      console.error("[ProjectDebug] loadManagers failed", e);
      setError("Failed to load managers: " + e.message);
    }
  }

  async function connectAs() {
    if (!selectedManager()) return;
    setAuthLoading(true);
    try {
      const result = await api.projects.generateToken(props.projectId, selectedManager());
      setAuthToken(result.token);
      setError("");
    } catch (e: any) {
      setError("Failed to generate token: " + e.message);
    }
    setAuthLoading(false);
  }

  async function load() {
    const token = authToken();
    if (!token) return;
    setLoading(true);
    try {
      const result = await api.projects.realtimeDebug(props.projectId, token);
      setData(result);
      setError("");
      setTokenExpired(false);
    } catch (e: any) {
      const msg = e.message || String(e);
      if (msg.includes("401") || msg.includes("Unauthorized") || msg.includes("Session revoked")) {
        setTokenExpired(true);
        setError("Token expired. Please reconnect to generate a new authentication token.");
        stopPolling();
      } else {
        setError(msg);
      }
    }
    setLoading(false);
  }

  let timer: ReturnType<typeof setInterval> | undefined;
  function stopPolling() {
    if (timer) { clearInterval(timer); timer = undefined; }
  }
  async function startPolling() {
    stopPolling();
    await load();
    if (!tokenExpired()) {
      timer = setInterval(load, 5000);
    }
  }
  onCleanup(() => stopPolling());

  function togglePause() {
    if (paused()) {
      setPaused(false);
      if (!tokenExpired()) startPolling();
    } else {
      setPaused(true);
      stopPolling();
    }
  }

  const connectAndInit = async () => {
    await connectAs();
    await startPolling();
  };

  onMount(() => {
    console.log("[ProjectDebug] component mounted", { projectId: props.projectId });
    loadManagers();
  });

  const recent = () => data()?.recentEvents ?? [];
  const hasAuth = () => !!authToken();

  return (
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <button
            onClick={() => { location.hash = "realtime/debug/"; }}
            class="text-sm text-gray-400 hover:text-gray-200 cursor-pointer"
          >
            &larr; Back to Projects
          </button>
          <span class="text-gray-600">|</span>
          <span class="text-sm text-gray-300 font-medium">Project: {props.projectId}</span>
        </div>
      </div>

      <Show when={tokenExpired()}>
        <div class="bg-yellow-900/30 border border-yellow-700 text-yellow-300 px-4 py-3 rounded-lg">
          <div class="flex items-center justify-between">
            <span class="text-sm">Token expired. The authentication session has timed out after 5 minutes.</span>
            <button
              onClick={connectAndInit}
              disabled={authLoading()}
              class="px-3 py-1.5 bg-yellow-600 text-white rounded text-sm font-medium hover:bg-yellow-500 disabled:opacity-50 cursor-pointer"
            >
              {authLoading() ? "Generating..." : "Reconnect"}
            </button>
          </div>
        </div>
      </Show>

      <div class="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
        <h3 class="text-sm font-medium text-gray-300">Authentication</h3>
        <div class="flex items-end gap-3">
          <div class="flex-1">
            <label class="block text-xs text-gray-500 mb-1">Act as manager</label>
            <select
              value={selectedManager()}
              onChange={(e) => setSelectedManager(e.target.value)}
              class="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-200 cursor-pointer"
              disabled={authLoading()}
            >
              <Show when={managers().length === 0}>
                <option value="">Loading managers...</option>
              </Show>
              {managers().map((m) => (
                <option value={m.userId}>
                  {m.name} ({m.role})
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={connectAndInit}
            disabled={authLoading() || !selectedManager()}
            class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          >
            {authLoading() ? "Generating..." : hasAuth() ? "Reconnect" : "Connect"}
          </button>
        </div>
        <Show when={hasAuth()}>
          <div class="flex items-center gap-2 text-xs">
            <span class={`w-2 h-2 rounded-full ${paused() ? "bg-yellow-500" : "bg-green-500"}`}></span>
            <span class={paused() ? "text-yellow-400" : "text-green-400"}>
              {paused() ? "Paused" : "Authenticated"}
            </span>
            <span class="text-gray-500">|</span>
            <span class="text-gray-400">{paused() ? "Paused" : "Every 5s"}</span>
            <label class="flex items-center gap-1 text-gray-400 cursor-pointer hover:text-gray-300">
              <input type="checkbox" checked={!paused()} onChange={togglePause} class="rounded cursor-pointer" />
              <span>Auto-refresh</span>
            </label>
            <button onClick={load} class="text-cyan-400 hover:text-cyan-300 cursor-pointer font-medium">
              Refresh
            </button>
          </div>
        </Show>
      </div>

      <Show when={error()}>
        <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
          {error()}
        </div>
      </Show>

      <Show when={loading() && !data()}>
        <div class="text-gray-400 text-sm">Loading...</div>
      </Show>

      <Show when={data()}>
        <div class="text-sm text-gray-400 mb-1">
          Total Connections: <span class="text-gray-200 font-medium">{data()!.totalConnections}</span>
        </div>

        <div class="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-gray-400 bg-gray-900/50">
                <th class="px-3 py-2 font-medium">User ID</th>
                <th class="px-3 py-2 font-medium">Role</th>
                <th class="px-3 py-2 font-medium">Searcher ID</th>
                <th class="px-3 py-2 font-medium">Connected</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-800/50">
              {data()!.connections.map((c) => (
                <tr class="text-gray-300 hover:bg-gray-900/30">
                  <td class="px-3 py-2 font-mono text-xs">{c.userId?.slice(0, 16)}...</td>
                  <td class="px-3 py-2">
                    <span class={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      c.role === "owner" || c.role === "admin" ? "bg-yellow-900/50 text-yellow-300" :
                      c.role === "manager" ? "bg-blue-900/50 text-blue-300" :
                      c.role === "searcher" ? "bg-green-900/50 text-green-300" :
                      "bg-gray-800 text-gray-400"
                    }`}>{c.role}</span>
                  </td>
                  <td class="px-3 py-2 font-mono text-xs">{c.searcherId ?? "—"}</td>
                  <td class="px-3 py-2 text-xs text-gray-400">{new Date(c.connectedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <h3 class="text-sm font-medium text-gray-300 mb-2">Channel Subscriptions</h3>
            <div class="bg-gray-900 border border-gray-800 rounded-lg p-3">
              <div class="space-y-1">
                {Object.entries(data()!.subscriptions).map(([channel, count]) => (
                  <div class="flex justify-between text-xs">
                    <span class="text-gray-400 font-mono">{channel}</span>
                    <span class="text-gray-200 font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-300 mb-2">SOS Rooms</h3>
            <div class="bg-gray-900 border border-gray-800 rounded-lg p-3">
              <div class="space-y-2">
                {Object.entries(data()!.sosRooms).length === 0 ? (
                  <div class="text-xs text-gray-500">None</div>
                ) : (
                  Object.entries(data()!.sosRooms).map(([room, info]) => (
                    <div class="text-xs">
                      <div class="text-gray-300 font-mono mb-0.5">{room}</div>
                      <div class="text-gray-500">
                        {info.memberCount} members: {info.members.map(m => m.slice(0, 12)).join(", ")}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <Show when={recent().length > 0}>
          <div>
            <h3 class="text-sm font-medium text-gray-300 mb-2">
              Routing Events
              <span class="text-gray-500 font-normal"> (last {recent().length})</span>
            </h3>
            <div class="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
              <table class="w-full text-sm">
                <thead>
                  <tr class="text-left text-gray-400 bg-gray-900/50">
                    <th class="px-3 py-2 font-medium">Time</th>
                    <th class="px-3 py-2 font-medium">Type</th>
                    <th class="px-3 py-2 font-medium">Subs</th>
                    <th class="px-3 py-2 font-medium">Room</th>
                    <th class="px-3 py-2 font-medium">Conns</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-800/50">
                  {recent().slice().reverse().map((e) => (
                    <tr class="text-xs text-gray-300 hover:bg-gray-900/30">
                      <td class="px-3 py-2 text-gray-400">{new Date(e.time).toLocaleTimeString()}</td>
                      <td class="px-3 py-2">
                        <span class={`px-1 py-0.5 rounded font-medium ${
                          e.type === "sos-resolved" ? "bg-green-900/50 text-green-300" : "bg-gray-800 text-gray-400"
                        }`}>{e.type}</span>
                      </td>
                      <td class="px-3 py-2">
                        <span class={e.searcherSubs === 0 ? "text-red-400 font-bold" : "text-green-400"}>{e.searcherSubs}</span>
                      </td>
                      <td class="px-3 py-2">{e.sosRoomSize}</td>
                      <td class="px-3 py-2">{e.totalConnections}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Show>

        <div>
          <button
            onClick={() => setExpanded(!expanded())}
            class="text-sm text-gray-500 hover:text-gray-300 cursor-pointer"
          >
            {expanded() ? "Hide" : "Show"} Raw JSON
          </button>
          <Show when={expanded()}>
            <pre class="mt-2 bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs text-gray-400 overflow-x-auto max-h-96">
              {JSON.stringify(data(), null, 2)}
            </pre>
          </Show>
        </div>
      </Show>
    </div>
  );
}
